import { ChevronLeft } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";

const PrivacyPage = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen brand-page-bg text-slate-900">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-0 h-96 w-96 rounded-full bg-[#dbe9ff] blur-3xl" />
        <div className="absolute top-20 right-0 h-80 w-80 rounded-full bg-[#f8efe2] blur-3xl" />
        <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[#e6f4ee] blur-3xl" />
      </div>

      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-[#fbfdff]/92 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/ichurch-logo.svg" alt="iChurch Data" className="h-8 w-8 rounded-xl object-cover" />
            <span className="text-lg font-semibold tracking-tight text-slate-900">iChurch Data</span>
          </Link>

          <div className="flex items-center gap-2">
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <Link to="/privacy">Privacy</Link>
            </Button>
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <Link to="/terms">Terms</Link>
            </Button>
            <Button variant="ghost" className="text-slate-700" asChild>
              <Link to="/login">Sign in</Link>
            </Button>
            <Button className="bg-[#1e3a8a] hover:bg-[#162d70]" asChild>
              <Link to="/register">Sign Up</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-4xl px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#1e3a8a] font-medium hover:underline mb-4">
          <ChevronLeft className="h-4 w-4" /> Back to Home
        </Link>
        <h1 className="text-3xl font-bold">Privacy Policy</h1>
        <p className="mt-2 text-sm text-slate-600">Effective Date: April 2026</p>

        <section className="mt-8 space-y-6 text-sm leading-6 text-slate-700">
          <p>
            This Privacy Policy describes how iChurch Data LLC ("iChurch Data," "we," "us," or "our") collects,
            uses, stores, discloses, and protects information in connection with our church management software
            platform (the "Service"). By accessing or using the Service, you and the organization you represent
            ("Customer") agree to the practices described in this policy.
          </p>

          <div>
            <h2 className="text-base font-semibold text-slate-900">1. Scope and Applicability</h2>
            <p>
              This policy applies to all individuals who access or use the iChurch Data platform, including church
              administrators, staff, and any person whose information is entered into the Service by a Customer
              organization. iChurch Data acts as a data processor on behalf of Customer organizations, which are
              the data controllers responsible for the personal information they input into the platform. We process
              personal data only on documented instruction from Customers, as set out in this policy and any
              applicable service agreement.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">2. Information We Collect</h2>
            <p>
              Customer-provided data includes account credentials, church profile data, member records, attendance
              logs, financial entries, giving records, and communication logs. We also automatically collect IP
              addresses, browser type, and device identifiers; log data including access times, pages viewed, and
              errors; usage patterns and feature interaction data; and cookies and similar tracking technologies
              used for authentication and session management. We do not knowingly collect personal information
              from children under 13. If you believe we have inadvertently collected such information, contact us
              immediately at contact@ichurchdata.com.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">3. Legal Basis for Processing</h2>
            <p>
              We process personal data on the following legal bases: (a) Contractual necessity, to deliver the
              Service as agreed in our Terms of Service; (b) Legitimate interests, to operate, secure, and improve
              the platform; (c) Legal obligation, to comply with applicable law, regulation, or lawful governmental
              request; and (d) Consent, where expressly obtained, such as for optional communications. Customer
              organizations are responsible for establishing their own lawful basis for collecting and processing
              member data under applicable law in their jurisdiction.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">4. How We Use Your Information</h2>
            <p>
              We use information collected solely to operate and improve the Service, including provision and
              delivery of platform features and functionality, user authentication and role-based access control,
              customer support and issue resolution, security monitoring, fraud prevention and abuse detection,
              service analytics and performance monitoring, billing and account management, and legal compliance
              and enforcement of our Terms of Service. We do not use Customer data for advertising, marketing to
              third parties, or any purpose unrelated to operating the Service.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">5. Disclosure of Information</h2>
            <p>
              We do not sell, rent, or trade personal data. We may disclose data only in limited circumstances:
              (a) to trusted service providers (for example cloud infrastructure and payment processors) under
              contractual data protection obligations; (b) to comply with legal requirements, court orders, or valid
              governmental requests, or to protect rights, property, or safety; (c) during business transfers such as
              merger, acquisition, or sale of assets, subject to the same privacy protections and notice before any
              transfer takes effect; and (d) with explicit written consent of the applicable Customer or data subject.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">6. Data Retention</h2>
            <p>
              We retain Customer data for as long as the account is active or as necessary to provide the Service.
              Upon termination of a service agreement, we retain data for 90 days to allow export, after which data
              is securely deleted or anonymized unless a longer retention period is required by law. Automatically
              collected technical data (logs and usage data) is retained for up to 12 months and then purged.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">7. Data Security</h2>
            <p>
              We implement commercially reasonable administrative, technical, and physical safeguards to protect
              data against unauthorized access, disclosure, alteration, or destruction, including encryption in transit
              (TLS) and at rest, role-based access controls and least-privilege principles, regular security
              assessments, and employee confidentiality obligations. No method of transmission or storage is 100%
              secure. If a data breach creates material risk to data subjects, we notify affected Customers without
              undue delay and in accordance with applicable breach notification laws.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">8. Your Rights</h2>
            <p>
              Depending on jurisdiction, you or your organization may have rights to access, correction, deletion,
              portability, restriction, and objection. Individual members whose data is stored by a church
              organization should direct privacy requests to their church administrator, as that organization is the
              data controller. To submit a request, contact contact@ichurchdata.com. We respond within 45 days
              and will notify you if additional time is needed.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">9. Customer Responsibilities</h2>
            <p>
              Customers are the data controllers for member information and bear independent legal
              responsibilities, including obtaining required consent, providing appropriate privacy notice, complying
              with applicable laws (including where applicable CCPA, PIPEDA, GDPR, or state-level equivalents),
              safeguarding credentials, and notifying iChurch Data and affected individuals of suspected breaches
              originating from their organization. iChurch Data LLC is not liable for a Customer's independent data
              collection or processing practices, or for failure to comply with applicable law.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">10. Third-Party Links and Integrations</h2>
            <p>
              The Service may contain links to or integrations with third-party websites or services. iChurch Data is
              not responsible for the privacy practices of those third parties. Review their policies independently
              before sharing information with them.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. If material changes are made, we notify
              Customers by email or a prominent in-product notice at least 30 days before changes take effect.
              Continued use of the Service after the effective date constitutes acceptance of the revised policy.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">12. Governing Law and Dispute Resolution</h2>
            <p>
              This Privacy Policy is governed by the laws of the State of South Carolina, without regard to conflict
              of law principles. Disputes arising under or in connection with this policy are subject to the exclusive
              jurisdiction of the state and federal courts located in Charleston County, South Carolina.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">13. Contact Us</h2>
            <p>iChurch Data LLC</p>
            <p>1260 Bees Ferry Rd, Unit 204, Charleston, SC 29414</p>
            <p>Email: contact@ichurchdata.com</p>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <p>For privacy inquiries, contact contact@ichurchdata.com.</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PrivacyPage;
